import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { trackBtnClick } from "@/lib/helper";

export const SwipeableButton = ({
  variant = "round",
  text = "Check Eligibility",
  screen,
}) => {
  const navigate = useNavigate();
  const [position, setPosition] = useState(10); // Initial position set to 10px from start
  const [dragging, setDragging] = useState(false);
  const [initialAnimation, setInitialAnimation] = useState(true);

  const buttonWidth = 300; // Total width of the button
  const sliderSize = 80; // Height of the square slider button (same as button height)
  const padding = 10; // Padding from the edges
  const maxPosition = buttonWidth - sliderSize + padding * 2 - 10; // Maximum position of the slider

  const startDrag = (event) => {
    event.preventDefault();
    setDragging(true);
    setInitialAnimation(false); // Stop the initial animation on drag
  };

  const stopDrag = (event) => {
    event.preventDefault();
    trackBtnClick("Check Eligibility", "Membership Benefits");
    navigate(screen);
    setDragging(false);
  };

  const handleDrag = (event) => {
    if (dragging) {
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const newPosition =
        clientX -
        event.target.parentElement.getBoundingClientRect().left -
        sliderSize / 2;
      setPosition(Math.max(0, Math.min(maxPosition, newPosition)));
    }
  };

  // Stop the initial animation after the first drag
  useEffect(() => {
    if (position > 10) {
      setInitialAnimation(false);
    }
  }, [position]);

  const isRound = variant === "round";
  const borderRadius = isRound ? "55px" : "55px";

  return (
    <div
      onMouseMove={handleDrag}
      onMouseLeave={stopDrag}
      onMouseUp={stopDrag}
      onTouchMove={handleDrag}
      onTouchEnd={stopDrag}
      style={{
        position: "relative",
        width: `${buttonWidth}px`,
        height: `${sliderSize}px`,
        borderRadius: borderRadius,
        background:
          position === 10
            ? "linear-gradient(90deg, #333333 32%, #101010 100%)"
            : position >= maxPosition - 2
              ? "#1987fb"
              : `linear-gradient(to right, rgba(25, 135, 251, 0.2) ${position + sliderSize / 2}px, #333333 ${position + sliderSize}px, #101010 100%)`, // Subtle color during swipe
        padding: `${padding}px`,
        cursor: dragging ? "grabbing" : "pointer",
        color: "white",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        zIndex: 50,
      }}
    >
      <div
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        style={{
          position: "absolute",
          left: `${position}px`,
          width: `${sliderSize - padding * 2}px`,
          height: `calc(100% - ${padding * 2}px)`,
          backgroundColor: "#FFF",
          borderRadius: isRound ? "50%" : "50%",
          cursor: "grab",
          top: `${padding}px`,
          zIndex: 20,
          animation: initialAnimation ? "bounce 1s infinite" : "none",
        }}
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "black",
            pointerEvents: "none", // Make the icon non-interactive
            userSelect: "none",
          }}
        />
      </div>
      <span
        style={{
          position: "absolute",
          left: "60%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          whiteSpace: "nowrap",
          fontFamily: "'Poppins', sans-serif",
          transition: "opacity 0.3s",
          opacity: position >= maxPosition - 2 ? 0 : 1,
        }}
      >
        {text}
      </span>
      <FontAwesomeIcon
        icon={faCheck}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          transition: "opacity 0.3s",
          opacity: position >= maxPosition - 2 ? 1 : 0,
          color: "white",
        }}
      />
      <style>
        {`
          @keyframes bounce {
            0%, 100% {
              transform: translateX(0);
            }
            50% {
              transform: translateX(4px);
            }
          }
        `}
      </style>
    </div>
  );
};

SwipeableButton.propTypes = {
  variant: PropTypes.oneOf(["default", "round"]),
};

SwipeableButton.defaultProps = {
  variant: "default",
};
